//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement } from 'react'

import { Box, Grid, Typography } from '@mui/material'
import { OtStoreState, useOtChainState, useOtSelector } from '@react/redux/store'
import {
  PoolFlexOverviewCTO,
  PoolFlexOverviewCTOSchema,
  PoolOverviewCTO,
  PoolOverviewCTOSchema,
} from '@shared/sharedTypes'
import { useParams } from 'react-router-dom'

import { FileCopy } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { LoanCTOSchema } from '@shared/LoanCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { SchemaDescription } from 'yup'
import { otAssert } from '@shared/conversions'

interface ContractAddrListItem {
  label: string
  value: string
}

export default function PoolContractAddresses({ role }: { role: RoleEnum }): React.ReactElement {
  const poolAddr = useParams().poolAddr as EthAddr
  const poolBaseOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  )

  const {
    chainConfiguration: { perimeterAddresses },
  } = useOtChainState()
  otAssert(perimeterAddresses, 'perimeterAddresses is not defined')
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null

  const handleCopyClick = (text: string): void => {
    navigator.clipboard.writeText(text).catch((_e) => {})
  }

  const contractAddresses: ContractAddrListItem[] = [
    {
      label: 'Loan Vault Address',
      value: poolBaseOverviewCTO.poolAddr,
    },
    {
      label: 'Liquidity Asset',
      value: poolBaseOverviewCTO.liquidityAssetAddr,
    },
  ]

  if (role !== RoleEnum.LENDER) {
    if (poolOverviewCTO) {
      contractAddresses.push({
        label: 'Active ' + ((LoanCTOSchema.describe().fields.fundingVaultAddr as SchemaDescription).label ?? ''),
        value: poolOverviewCTO.activeLoanState.fundingVaultAddr,
      })
    }
    if (poolOverviewCTO) {
      contractAddresses.push({
        label: 'Requested ' + ((LoanCTOSchema.describe().fields.fundingVaultAddr as SchemaDescription).label ?? ''),
        value: poolOverviewCTO.requestedLoanState.fundingVaultAddr,
      })
    }
    contractAddresses.push({
      label: (PoolOverviewCTOSchema.describe().fields.withdrawControllerAddr as SchemaDescription).label ?? '',
      value: poolBaseOverviewCTO.withdrawControllerAddr,
    })
    contractAddresses.push({
      label: (PoolOverviewCTOSchema.describe().fields.feeVaultAddr as SchemaDescription).label ?? '',
      value: poolBaseOverviewCTO.feeVaultAddr,
    })
    contractAddresses.push({
      label: (PoolOverviewCTOSchema.describe().fields.poolControllerAddr as SchemaDescription).label ?? '',
      value: poolBaseOverviewCTO.poolControllerAddr,
    })
    contractAddresses.push({
      label: (PoolOverviewCTOSchema.describe().fields.poolAdminAddr as SchemaDescription).label ?? '',
      value: poolBaseOverviewCTO.poolAdminAddr,
    })
    contractAddresses.push({
      label: (PoolOverviewCTOSchema.describe().fields.borrowerManagerAddr as SchemaDescription).label ?? '',
      value: poolBaseOverviewCTO.borrowerManagerAddr,
    })
    contractAddresses.push({
      label: (PoolOverviewCTOSchema.describe().fields.borrowerVaultAddr as SchemaDescription).label ?? '',
      value: poolBaseOverviewCTO.borrowerVaultAddr,
    })
    contractAddresses.push({
      label: (PoolOverviewCTOSchema.describe().fields.borrowerWalletAddr as SchemaDescription).label ?? '',
      value: poolBaseOverviewCTO.borrowerWalletAddr,
    })
    if (poolOverviewCTO) {
      contractAddresses.push({
        label: 'Active ' + ((LoanCTOSchema.describe().fields.loanAddr as SchemaDescription).label ?? ''),
        value: poolOverviewCTO.activeLoanState.loanAddr,
      })
    }
    if (poolOverviewCTO) {
      contractAddresses.push({
        label: 'Requested ' + ((LoanCTOSchema.describe().fields.loanAddr as SchemaDescription).label ?? ''),
        value: poolOverviewCTO.requestedLoanState.loanAddr,
      })
    }
    if (poolFlexOverviewCTO) {
      contractAddresses.push({
        label: (PoolFlexOverviewCTOSchema.describe().fields.businessDayRegistryAddr as SchemaDescription).label ?? '',
        value: poolFlexOverviewCTO.businessDayRegistryAddr,
      })
    }
    if (poolFlexOverviewCTO) {
      contractAddresses.push({
        label: 'Service Configuration Address',
        value: perimeterAddresses.ServiceConfiguration,
      })
    }
  }

  const gridItems: ReactElement[] = []
  contractAddresses.forEach((addr) => {
    gridItems.push(
      <Grid item xs={4} key={addr.label}>
        <Typography variant="body1" align={'left'} component="div" sx={{ paddingTop: '5px', flexGrow: 1 }}>
          {addr.label}
        </Typography>
      </Grid>
    )
    gridItems.push(
      <Grid item xs={8} key={addr.label + '2'}>
        <div
          style={{
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" component="div" sx={{ flexGrow: 1 }} align="left">
            {addr.value}
          </Typography>
          <IconButton
            onClick={() => {
              handleCopyClick(addr.value)
            }}
          >
            <FileCopy />
          </IconButton>
        </div>
      </Grid>
    )
  })

  return (
    <Box>
      <Grid container spacing={1}>
        {gridItems}
      </Grid>
    </Box>
  )
}
